import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { OrderData } from "../types/order";
import { fetchCustomerLedger, fetchOrderById, updateOrderStatusApi } from "./orderService";

export const useOrderDetails = (orderId?: string) => {
  const [order, setOrder] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [outstandingAmount, setOutstandingAmount] = useState(0);

  /** Fetch Order Details */
  const fetchOrderDetails = useCallback(async () => {
    if (!orderId) return;

    try {
      setIsLoading(true);
      const orderData = await fetchOrderById(orderId);
      setOrder(orderData);

      if (orderData.Customerid) {
        const balance = await fetchCustomerLedger(orderData.Customerid);
        setOutstandingAmount(balance);
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  /** Update Order Status */
  const updateOrderStatus = useCallback(
    async (status: string, adjustType: string = "refund") => {
      if (!orderId) return false;

      try {
        await updateOrderStatusApi(orderId, status, adjustType);
        setOrder((prev) => (prev ? { ...prev, OrderStatus: status } : prev));
        return true;
      } catch (error) {
        console.error("Error updating order status:", error);
        toast.error("Failed to update order status");
        return false;
      }
    },
    [orderId]
  );

  /** Local Order Update (for components) */
  const updateOrder = useCallback((updater: (prev: OrderData | null) => OrderData | null) => {
    setOrder(updater);
  }, []);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  return {
    order,
    isLoading,
    outstandingAmount,
    fetchOrderDetails,
    updateOrderStatus,
    updateOrder,
    setOrder,
  };
};
